import dayjs from 'dayjs'
import { useCallback } from 'react'
import { useDispatch, shallowEqual, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { showError } from 'slices/notificationSlice'
import { selectTenantsStatus } from 'slices/tenantsSlice'
import { checkWorkspaceExists } from 'slices/workspacesSlice'

import useBusinessTime from './useBusinessTime'

type RelativeDateRange = {
  // 当日から数えて何日目かを指定する（過去の場合は-にする）
  fromDays?: number
  toDays?: number
}

const useDateChange = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { tenant } = useSelector(selectTenantsStatus, shallowEqual)
  const { pathname } = useLocation()
  const [, setSearchParams] = useSearchParams()
  const { getWorkDate } = useBusinessTime()

  const checkTenantCreated = useCallback(
    (newDate: string) => {
      if (!tenant) {
        return
      }
      if (dayjs(newDate).isBefore(tenant.createdAt, 'day')) {
        dispatch(showError({ errorMessage: 'テナント作成日以前の日付は選択できません' }))
        return
      }
      setSearchParams(
        params => {
          params.set('date', newDate)
          return params
        },
        { replace: true }
      )
    },
    [dispatch, tenant, setSearchParams]
  )

  const checkWorkspaceCreated = useCallback(
    (newDate: string, workspaceId: number, targetPathnameWithDate?: string) => {
      const callback = (workspaceExists: boolean) => {
        if (workspaceExists) {
          navigate(targetPathnameWithDate || `${pathname}?date=${newDate}`)
        } else {
          dispatch(showError({ errorMessage: '指定された日付のワークスペースが存在しません' }))
        }
      }
      dispatch(checkWorkspaceExists(workspaceId, newDate, callback))
    },
    [dispatch, navigate, pathname]
  )

  const createDateFilter = (dateRange: RelativeDateRange) => {
    return (newDate: Date) => {
      const target = dayjs(newDate)
      const workDate = dayjs(getWorkDate(dayjs().format('YYYY-MM-DD')))
      return (
        (dateRange.fromDays === undefined || target.isSameOrAfter(workDate.add(dateRange.fromDays, 'day'))) &&
        (dateRange.toDays === undefined || target.isSameOrBefore(workDate.add(dateRange.toDays, 'day')))
      )
    }
  }

  return { checkTenantCreated, checkWorkspaceCreated, createDateFilter }
}
export default useDateChange
